import Cookies from 'js-cookie'

const VOUCHERIFY_PROMO_COOKIE = 'gcg_voucherify_promo_code'
const VOUCHERIFY_PROMO_REMOVAL_COOKIE = 'gcg_voucherify_promo_removed'
const VOUCHERIFY_COOKIE_EXPIRATION_DAYS = 1

const getCookieDomainOptions = () => {
    if (typeof window === 'undefined') {
        return {}
    }

    const configuredDomain = process.env.REACT_APP_VOUCHERIFY_COOKIE_DOMAIN

    if (configuredDomain) {
        return { domain: configuredDomain }
    }

    const hostname = window.location.hostname || ''

    if (
        hostname === 'giftcardgranny.com' ||
        hostname.endsWith('.giftcardgranny.com')
    ) {
        return { domain: '.giftcardgranny.com' }
    }

    if (
        hostname === 'giftcardgrannystage.com' ||
        hostname.endsWith('.giftcardgrannystage.com')
    ) {
        return { domain: '.giftcardgrannystage.com' }
    }

    return {}
}

export const applyVoucherifyPreviewToCart = (
    queryClient,
    promoCode,
    voucherifyResponse
) => {
    const discountAmountRaw = Number(
        voucherifyResponse?.discount_amount ?? 0
    )
    const discountAmount = Number(discountAmountRaw.toFixed(2))

    if (Number.isNaN(discountAmount) || discountAmount <= 0) {
        throw new Error(
            voucherifyResponse?.error_message || 'Promo code cannot be applied'
        )
    }

    queryClient.setQueryData('cart', (previous) => {
        if (!previous?.cart) {
            return previous
        }

        const previousCart = previous.cart
        const previousOrderTotal = Number(previousCart.order_total ?? 0)
        const existingDiscount = Number(
            previousCart?.promo_code_redemption?.total_discount ?? 0
        )
        const orderTotalBeforeDiscount = previousOrderTotal + existingDiscount

        const promoCodeRedemption = {
            redemption_code: promoCode,
            total_discount: discountAmount,
            load_value_discount: discountAmount,
            customization_fee_discount: 0,
            processing_fee_discount: 0,
            shipping_fee_discount: 0,
            greeting_card_fee_discount: 0,
            has_qualified_items: true,
            terms: null,
        }

        const updatedCart = {
            ...previousCart,
            promo_code_redemption: promoCodeRedemption,
            total_discount: discountAmount,
            order_total: Math.max(
                0,
                orderTotalBeforeDiscount - discountAmount
            ),
        }

        return {
            ...previous,
            cart: updatedCart,
            voucherify: voucherifyResponse,
        }
    })

    return discountAmount
}

export const removeVoucherifyPreviewFromCart = (queryClient) => {
    queryClient.setQueryData('cart', (previous) => {
        if (!previous?.cart) {
            return previous
        }

        const previousCart = previous.cart
        const discountAmount = Number(
            previousCart?.promo_code_redemption?.total_discount ?? 0
        )

        const recalculatedOrderTotal = Math.max(
            0,
            Number(previousCart?.order_total ?? 0) + discountAmount
        )

        return {
            ...previous,
            cart: {
                ...previousCart,
                promo_code_redemption: null,
                total_discount: 0,
                order_total: recalculatedOrderTotal,
            },
            voucherify: null,
        }
    })
}

export const markVoucherifyPromoCodeManuallyRemoved = () => {
    const domainOptions = getCookieDomainOptions()

    Cookies.set(VOUCHERIFY_PROMO_REMOVAL_COOKIE, '1', {
        path: '/',
        expires: VOUCHERIFY_COOKIE_EXPIRATION_DAYS,
        ...domainOptions,
    })
}

export const clearVoucherifyPromoCodeManualRemoval = () => {
    const domainOptions = getCookieDomainOptions()

    Cookies.remove(VOUCHERIFY_PROMO_REMOVAL_COOKIE, { path: '/' })

    if (domainOptions.domain) {
        Cookies.remove(VOUCHERIFY_PROMO_REMOVAL_COOKIE, {
            path: '/',
            domain: domainOptions.domain,
        })
    }
}

export const hasManuallyRemovedVoucherifyPromoCode = () => {
    return Cookies.get(VOUCHERIFY_PROMO_REMOVAL_COOKIE) === '1'
}

export const rememberVoucherifyPromoCode = (promoCode) => {
    if (!promoCode) {
        return
    }

    clearVoucherifyPromoCodeManualRemoval()

    const domainOptions = getCookieDomainOptions()

    Cookies.set(VOUCHERIFY_PROMO_COOKIE, promoCode, {
        path: '/',
        expires: VOUCHERIFY_COOKIE_EXPIRATION_DAYS,
        ...domainOptions,
    })
}

export const forgetVoucherifyPromoCode = () => {
    const domainOptions = getCookieDomainOptions()

    Cookies.remove(VOUCHERIFY_PROMO_COOKIE, { path: '/' })

    if (domainOptions.domain) {
        Cookies.remove(VOUCHERIFY_PROMO_COOKIE, {
            path: '/',
            domain: domainOptions.domain,
        })
    }
}

export const readVoucherifyPromoCode = () => {
    return Cookies.get(VOUCHERIFY_PROMO_COOKIE) || null
}
